import type { InternalRequest } from '../client.js';

type Params = Record<string, string | number | boolean | undefined | null>;

function qsFrom(params?: Params): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of entries) usp.set(k, String(v));
  return `?${usp}`;
}

export function orgsEndpoints(req: InternalRequest) {
  return {
    list: (params?: Params) => req.request<unknown[]>(`/orgs${qsFrom(params)}`),
    get: (id: string) => req.request<unknown>(`/orgs/${id}`),
    create: (data: Record<string, unknown>) =>
      req.request<unknown>('/orgs', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Record<string, unknown>) =>
      req.request<unknown>(`/orgs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    /** Deleting an org requires echoing its exact name — the server 400s
     * `confirm_name_mismatch` otherwise (MyCRM TASK-281). */
    delete: (id: string, confirmName: string) =>
      req.request<void>(`/orgs/${id}`, { method: 'DELETE', body: JSON.stringify({ confirmName }) }),
    /** Device-to-org mapping (org-scoped device list). */
    devices: {
      list: (orgId: string) => req.request<unknown[]>(`/orgs/${orgId}/devices`),
      add: (orgId: string, deviceIds: number[]) =>
        req.request<unknown>(`/orgs/${orgId}/devices`, {
          method: 'POST',
          body: JSON.stringify({ deviceIds }),
        }),
      remove: (orgId: string, deviceIds: number[]) =>
        req.request<unknown>(`/orgs/${orgId}/devices`, {
          method: 'DELETE',
          body: JSON.stringify({ deviceIds }),
        }),
    },
    /** Organization members (invite / remove). */
    members: {
      list: (orgId: string) => req.request<unknown[]>(`/orgs/${orgId}/members`),
      add: (orgId: string, data: Record<string, unknown>) =>
        req.request<unknown>(`/orgs/${orgId}/members`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      remove: (orgId: string, userId: string) =>
        req.request<void>(`/orgs/${orgId}/members/${userId}`, { method: 'DELETE' }),
    },
    /**
     * Instance-Admin-only toggle deciding whether this org may spend the
     * PLATFORM owner's AI provider key when it has none of its own
     * (`PATCH /api/orgs/:id/ai-fallback`, server-side in
     * `server/src/orgs/org.routes.ts`). Merges atomically server-side —
     * supplying only one feature leaves the other untouched — and returns
     * the merged `{ aiFallback: {...} }`, not the whole org row.
     */
    setAiFallback: (orgId: string, body: Record<string, boolean>) =>
      req.request<{ aiFallback: Record<string, boolean> }>(`/orgs/${orgId}/ai-fallback`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  };
}
